import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import useModal from "components/modal/useModal";
import UseContext from "hooks/useContext";
import Modalheader from "components/modal/components/header";
import ModalBody from "components/modal/components/body";
import ModalFooter from "components/modal/components/footer";
import Button from "components/button";
import LoadingButton from "components/button/loading";
import ApiDataBase from "server";
import {
    buildDuplicateTemplateName,
    getTemplateIntituleSuffix,
    isTemplateNameTaken,
    templateIntitulePrefix,
} from "pages/missionTemplates/utils/templateIntitule";

const DuplicateMissionTemplate = () => {
    const { toggle } = useModal();
    const {
        pendingDuplicateMissionTemplate,
        setPendingDuplicateMissionTemplate,
        missionTemplates,
        refreshMissionTemplates,
        setRefreshMissionTemplates,
        previewMissionTemplate,
        setPreviewMissionTemplate,
        setModalType,
        language,
    } = UseContext();

    const source = pendingDuplicateMissionTemplate;
    const [name, setName] = useState(() =>
        buildDuplicateTemplateName(source?.name, missionTemplates)
    );
    const [isLoading, setIsLoading] = useState(false);

    // Same rule as the wizard: the qualification opens the name and cannot be
    // touched, only what follows the dash is typed.
    const namePrefix = templateIntitulePrefix(source?.qualification);
    const nameSuffix = getTemplateIntituleSuffix(name, source?.qualification);
    const onSuffixChange = (suffix) => setName(namePrefix + suffix);

    const isNameTaken = useMemo(
        () => isTemplateNameTaken(name, missionTemplates),
        [name, missionTemplates]
    );
    const canSubmit = !!name.trim() && !isNameTaken && !!source?._id;

    // Opened from the preview panel: cancelling goes back to it.
    const handleCancel = () => {
        setPendingDuplicateMissionTemplate(null);
        if (previewMissionTemplate?._id) {
            setModalType("missionTemplate_preview");
            return;
        }
        toggle();
    };

    const handleConfirm = () => {
        if (!canSubmit) return;
        setIsLoading(true);
        ApiDataBase.postDuplicateMissionTemplate(
            { id: source._id, name: name.trim() },
            (created) => {
                setPendingDuplicateMissionTemplate(null);
                setRefreshMissionTemplates(!refreshMissionTemplates);
                toast.success(
                    language.getValue(
                        "missionTemplate.duplicate.done",
                        "Fiche de poste dupliquée"
                    )
                );
                // The api answers with the raw document, whose location is still
                // an id: the preview needs the read shape, so it is re-read.
                ApiDataBase.getMissionTemplate(
                    { id: created?._id },
                    (template) => {
                        setIsLoading(false);
                        setPreviewMissionTemplate(template);
                        setModalType("missionTemplate_preview");
                    },
                    (err) => {
                        setIsLoading(false);
                        console.error(err);
                        toggle();
                    }
                );
            },
            (err) => {
                setIsLoading(false);
                console.error(err);
                if (
                    err?.response?.data?.message === "Template name already used"
                ) {
                    toast.error("Une fiche de poste porte déjà ce nom");
                    return;
                }
                toast.error(
                    language.getValue(
                        "missionTemplate.duplicate.failed",
                        "Duplication impossible"
                    )
                );
            }
        );
    };

    return (
        <>
            <Modalheader hide={handleCancel} hideShow={true}>
                {language.getValue(
                    "missionTemplate.duplicate.title",
                    "Dupliquer la fiche de poste"
                )}
            </Modalheader>
            <ModalBody>
                <div className={"flex flex-col gap-2"}>
                    <p className={"ft-sm text-gray-700"}>
                        {language.getValue(
                            "missionTemplate.duplicate.description",
                            "La copie reprend tout le contenu de la fiche. Donnez-lui un nom."
                        )}
                    </p>
                    <label className={"flex flex-col gap-1"}>
                        <span className={"ft-sm text-gray-700"}>
                            {language.getValue(
                                "missionTemplate.duplicate.nameLabel",
                                "Nom de la nouvelle fiche"
                            )}
                        </span>
                        <div
                            className={`flex w-full min-h-10 rounded-lg border overflow-hidden ${
                                isNameTaken
                                    ? "border-red-500"
                                    : "border-gray-300"
                            }`}
                        >
                            {namePrefix && (
                                <span
                                    className="shrink-0 max-w-[55%] px-3 py-2 text-sm leading-5 text-gray-800 bg-gray-100 border-r border-gray-200 truncate"
                                    title={namePrefix}
                                >
                                    {namePrefix}
                                </span>
                            )}
                            <input
                                type="text"
                                autoFocus
                                value={nameSuffix}
                                onChange={(e) => onSuffixChange(e.target.value)}
                                placeholder="Complément"
                                className="flex-1 min-w-0 h-10 pl-3 pr-3 ft-b placeholder-gray-600 border-0 focus:outline-none focus:ring-0"
                            />
                        </div>
                    </label>
                    {isNameTaken && (
                        <p className={"ft-sm text-red-500"}>
                            Une fiche de poste porte déjà ce nom.
                        </p>
                    )}
                </div>
            </ModalBody>
            <ModalFooter>
                <Button size={"LG"} color={"SECONDARY"} onClick={handleCancel}>
                    Annuler
                </Button>
                {isLoading ? (
                    <LoadingButton size={"LG"}>Duplication…</LoadingButton>
                ) : (
                    <Button
                        size={"LG"}
                        color={canSubmit ? "PRIMARY" : "DISABLED"}
                        onClick={handleConfirm}
                        disabled={!canSubmit}
                    >
                        {language.getValue(
                            "missionTemplate.duplicate.confirm",
                            "Dupliquer"
                        )}
                    </Button>
                )}
            </ModalFooter>
        </>
    );
};

export default DuplicateMissionTemplate;
